import { useContext, useEffect, useRef } from "react";

import { addSelectedEntities, removeSelectedEntity, setSelectedEntities } from "../../Context/ViewerContext/Reducer";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "../../Context/XktContext/XktContextProvider";
interface XktCanvas {
    canvas: HTMLCanvasElement;
}
interface SceneWithCanvas {
    canvas: XktCanvas;
}

interface PickerProps {
    isLongPressing: React.MutableRefObject<boolean>;
}

const Picker = ({ isLongPressing }: PickerProps) => {
    const { state: viewerState, dispatch } = useContext(ViewerContext);
    const { state } = useContext(XktContext);
    const mousePosRef = useRef({ x: 0, y: 0 });
    useEffect(() => {
        if (!state.viewer) return;
        const swc = state.viewer.scene as unknown as SceneWithCanvas;
        const mouseDown = (event: MouseEvent) => {
            if (event.button == 0) {
                mousePosRef.current = { x: event.clientX, y: event.clientY };
            }
        };

        const onTouchstart = (event: TouchEvent) => {
            if (event.touches.length === 1) {
                const touch = event.touches[0];
                mousePosRef.current = { x: touch.clientX, y: touch.clientY };
            }
        };

        swc.canvas.canvas.addEventListener("mousedown", mouseDown);
        swc.canvas.canvas.addEventListener("touchstart", onTouchstart);

        const mouseUp = (event: MouseEvent) => {
            if (viewerState.addingSectionPlane) return;
            if (!state.viewer) return;
            const currentPos = { x: event.clientX, y: event.clientY };
            const diff = Math.abs(currentPos.x - mousePosRef.current.x + currentPos.y - mousePosRef.current.y);
            if (diff > 1) {
                return;
            }
            mousePosRef.current = { x: event.clientX, y: event.clientY };
            if (!viewerState.pickerIsEnabled) return;
            if (event.button == 0) {
                const pickResult = state.viewer.scene.pick({
                    canvasPos: [event.clientX, event.clientY],
                    excludeEntities: viewerState.invisibleEntities,
                });
                if (pickResult) {
                    const ent = pickResult.entity;
                    if (ent) {
                        if (event.shiftKey) {
                            if (ent.selected) {
                                dispatch(removeSelectedEntity([ent.id as string]));
                            }
                        } else if (event.ctrlKey) {
                            if (!ent.selected) {
                                dispatch(addSelectedEntities([ent.id as string]));
                            }
                        } else {
                            if (ent.selected) {
                                dispatch(setSelectedEntities([]));
                            } else {
                                dispatch(setSelectedEntities([ent.id as string]));
                            }
                        }
                    } else {
                        dispatch(setSelectedEntities([]));
                    }
                } else {
                    dispatch(setSelectedEntities([]));
                }
            }
        };

        const onTouchEnd = (event: TouchEvent) => {
            if (isLongPressing.current) {
                isLongPressing.current = false;
                return;
            }

            if (viewerState.addingSectionPlane) return;
            if (!state.viewer) return;
            if (event.touches.length > 1) return;

            const touch = event.changedTouches[0];
            const currentPos = { x: touch.clientX, y: touch.clientY };
            const diff = Math.abs(currentPos.x - mousePosRef.current.x + currentPos.y - mousePosRef.current.y);

            if (diff > 1) {
                return;
            }

            mousePosRef.current = { x: touch.clientX, y: touch.clientY };
            if (!viewerState.pickerIsEnabled) return;

            const pickResult = state.viewer.scene.pick({
                canvasPos: [touch.clientX, touch.clientY],
                excludeEntities: viewerState.invisibleEntities,
            });
            if (pickResult) {
                const ent = pickResult.entity;
                if (ent) {
                    if (event.shiftKey) {
                        if (ent.selected) {
                            dispatch(removeSelectedEntity([ent.id as string]));
                        }
                    } else if (event.ctrlKey) {
                        if (!ent.selected) {
                            dispatch(addSelectedEntities([ent.id as string]));
                        }
                    } else {
                        if (ent.selected) {
                            dispatch(setSelectedEntities([]));
                        } else {
                            dispatch(setSelectedEntities([ent.id as string]));
                        }
                    }
                } else {
                    dispatch(setSelectedEntities([]));
                }
            } else {
                dispatch(setSelectedEntities([]));
            }
        };

        swc.canvas.canvas.addEventListener("mouseup", mouseUp);
        swc.canvas.canvas.addEventListener("touchend", onTouchEnd);
        return () => {
            swc.canvas.canvas.removeEventListener("mousedown", mouseDown);
            swc.canvas.canvas.removeEventListener("mouseup", mouseUp);
            swc.canvas.canvas.removeEventListener("touchstart", onTouchstart);
            swc.canvas.canvas.removeEventListener("touchend", onTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.viewer, viewerState.pickerIsEnabled, viewerState.addingSectionPlane]);

    return <></>;
};

export default Picker;
